import { useEffect, type ReactNode } from 'react';
import { useMyTenants, useSwitchTenant } from '../api/queries';
import { lastTenant, useSession } from '../auth/session';
import { useTranslate } from '../i18n/useTranslate';
import { ErrorView } from '../ui/ErrorView';

/**
 * Aktif kurumu belirler (SEOAI-78).
 *
 * NEDEN VAR: giriş jetonu kurum TAŞIMAZ. Backend'de `User` bilerek tenant'a ait
 * değildir ("Kullanıcı tenant'a AİT DEĞİLDİR; üyelik ayrı"); `LoginHandler`
 * jetonu `$user->tenantId()` ile üretir ve o HER ZAMAN null'dır. Doğrulandı:
 * kurumun SAHİBİ olan kullanıcı bile giriş sonrası `/projects` çağırınca
 * `SHARED.AUTH.FORBIDDEN` alıyor. Kurumlu jetonu üreten tek yer `switch-tenant`.
 *
 * DAVRANIŞ — kullanıcıya gereksiz soru sorulmaz:
 *   sunucu bildirdi → hiç soru sorulmaz, liste bile çekilmez (SEOAI-80)
 *   1 kurum        → otomatik seçilir, hiçbir ekran görünmez
 *   çok kurum + son kullanılan hatırlanıyorsa → otomatik seçilir
 *   çok kurum, ilk kez → seçim ekranı (tek sefer; sonra hatırlanır)
 *   0 kurum        → açıklama (davet edilmeleri gerekir; kurum oluşturmak
 *                    platform yetkisi ister, SEOAI-50)
 */
export function TenantGate({ children }: { children: ReactNode }) {
  const t = useTranslate();
  const tenantId = useSession((s) => s.tenantId);
  // Kurum belliyse liste GEREKMEZ (SEOAI-80): sunucu bağlamı zaten verdi.
  const tenants = useMyTenants(tenantId === null);
  const switchTenant = useSwitchTenant();

  const options = tenants.data;
  const switchMutate = switchTenant.mutate;
  const isSwitching = switchTenant.isPending;

  useEffect(() => {
    if (tenantId !== null || !options || options.length === 0 || isSwitching) return;

    // Tek kurum: sorulacak bir şey yok. Çok kurum: son kullanılan hâlâ üye
    // olduğumuz bir kurumsa onu uygula — üyelik kaldırılmış olabilir.
    const remembered = lastTenant();
    const target =
      options.length === 1
        ? options[0]
        : options.find((tenant) => tenant.tenantId === remembered);

    if (target) switchMutate(target.tenantId);
  }, [tenantId, options, isSwitching, switchMutate]);

  if (tenantId !== null) return <>{children}</>;

  if (tenants.isPending) return <p>{t('tenant.loading')}</p>;
  if (tenants.isError) return <ErrorView error={tenants.error} onRetry={() => void tenants.refetch()} />;

  if (tenants.data.length === 0) {
    return (
      <section className="card">
        <h1>{t('tenant.noneTitle')}</h1>
        <p>{t('tenant.noneBody')}</p>
      </section>
    );
  }

  // Otomatik seçim sürüyor (tek kurum ya da hatırlanan kurum): seçim ekranını
  // bir an gösterip kaybetmek titreme olurdu.
  if (isSwitching || tenants.data.length === 1 || tenants.data.some((x) => x.tenantId === lastTenant())) {
    return <p>{t('tenant.switching')}</p>;
  }

  return (
    <section className="card">
      <h1>{t('tenant.chooseTitle')}</h1>
      <p>{t('tenant.chooseBody')}</p>

      <ul className="list">
        {tenants.data.map((tenant) => (
          <li key={tenant.tenantId}>
            <button type="button" disabled={isSwitching} onClick={() => switchMutate(tenant.tenantId)}>
              {tenant.name}
            </button>
            {tenant.roleName && <span className="badge">{tenant.roleName}</span>}
          </li>
        ))}
      </ul>

      {switchTenant.isError && <ErrorView error={switchTenant.error} />}
    </section>
  );
}
